// An invented textbook for the PDF version of the description mockup. Nothing is read
// from the chosen file: the page picker and the text check always show these pages.

export type SamplePageKind = 'cover' | 'front' | 'guidance' | 'contents' | 'opener' | 'text' | 'vocabulary' | 'grammar' | 'exercise' | 'activity' | 'index';

export interface OcrIssue {
    id: string;
    message: string;
    /** The text as OCR read it, and the printed text. A note without them has no single fix. */
    wrong?: string;
    right?: string;
}

export interface SamplePdfPage {
    pdfPage: number;
    printedLabel: string;
    kind: SamplePageKind;
    /** What the printed page says; its first line is the page heading. */
    text: string;
    scanned: boolean;
    issues: OcrIssue[];
}

export interface SamplePdfSection {
    id: string;
    title: string;
    english: string;
    firstPage: number;
    lastPage: number;
    group: 'Front matter' | 'Lessons' | 'Back matter';
    forGuidance?: boolean;
    classHours?: string;
}

export interface SamplePdfFile {
    id: string;
    name: string;
    sizeLabel: string;
    pages: SamplePdfPage[];
    sections: SamplePdfSection[];
}

interface SampleUnit {
    title: string;
    english: string;
    classHours: string;
    vocabulary: [string, string, string, string][];
    grammar: { pattern: string; note: string; examples: string[] }[];
    text1: string[];
    text2: string[];
    exercises1: string[];
    exercises2: string[];
    activity: string[];
    homework: string[];
    selfCheck: string[];
}

const UNIT_PAGES = 12;
const FIRST_UNIT_PAGE = 11;
const CHINESE_NUMBERS = ['一', '二', '三', '四', '五', '六', '七', '八'];

function lessonActivity(english: string): string[] {
    return ['两人一组，用本课的生词和语法编一个对话，然后表演。', `Work in pairs: write a short dialogue about ${english.toLowerCase()} with this lesson's words and grammar, then perform it.`];
}

const defaultHomework = ['1. 听录音，跟读课文。Listen to the recording and read the texts aloud.', '2. 用本课语法写五个句子。Write five sentences with this lesson\'s grammar.'];

const units: SampleUnit[] = [
    {
        title: '去饭馆', english: 'Eating out', classHours: '4–6',
        vocabulary: [
            ['饭馆', 'fànguǎn', 'n.', 'restaurant'], ['菜单', 'càidān', 'n.', 'menu'], ['服务员', 'fúwùyuán', 'n.', 'waiter; server'],
            ['点菜', 'diǎn cài', 'v.o.', 'to order dishes'], ['饺子', 'jiǎozi', 'n.', 'dumplings'], ['米饭', 'mǐfàn', 'n.', 'cooked rice'],
            ['盘', 'pán', 'm.', 'plate (measure word)'], ['碗', 'wǎn', 'm.', 'bowl (measure word)'], ['杯', 'bēi', 'm.', 'cup; glass (measure word)'],
            ['茶', 'chá', 'n.', 'tea'], ['好吃', 'hǎochī', 'adj.', 'delicious'], ['买单', 'mǎidān', 'v.', 'to pay the bill'],
        ],
        grammar: [
            { pattern: '请给我…… (polite request)', note: '请给我 + a noun phrase asks politely for something.', examples: ['服务员，请给我菜单。', '请给我一杯茶。'] },
            { pattern: '来 + 数量 + 名词 (ordering food)', note: '来 is used when ordering food and drinks; a measure word comes before the noun.', examples: ['来一盘饺子。', '我们来两碗米饭、一杯茶。'] },
        ],
        text1: ['A：欢迎光临！几位？', 'B：两位。', 'A：请坐。这是菜单。', 'B：服务员，我们点菜。来一盘饺子，两碗米饭。', 'A：好的。要喝点儿什么？', 'B：请给我一杯茶。'],
        text2: ['A：饺子好吃吗？', 'B：很好吃！', 'A：服务员，买单。', 'B：好的，一共三十八块。'],
        exercises1: ['一、用“请给我……”说一说 Make polite requests', '1. 菜单', '2. 一杯茶', '3. 两碗米饭', '二、完成对话 Complete the dialogue', 'A：你们要点什么？', 'B：________。'],
        exercises2: ['三、看菜单点菜 Order from the menu', '饺子 | 一盘 | 二十五块', '米饭 | 一碗 | 三块', '茶 | 一杯 | 十块'],
        activity: ['饭馆点菜 Ordering in a restaurant', '三人一组：一人当服务员，两人当客人。客人看菜单点菜，服务员记下来，最后买单。', 'Groups of three: one waiter and two customers. The customers order from the menu, the waiter writes the order down, then they pay the bill.', '建议时间：15 分钟 Suggested time: 15 minutes'],
        homework: ['1. 听录音，跟读课文一。Listen to the recording and read Text 1 aloud.', '2. 写一份你喜欢的饭馆的菜单。Write a menu for a restaurant you like.'],
        selfCheck: ['□ 我能在饭馆点菜。I can order food in a restaurant.', '□ 我能用“请给我……”客气地要东西。I can make polite requests with 请给我…….'],
    },
    {
        title: '生日聚会', english: 'A birthday party', classHours: '3–4',
        vocabulary: [['生日', 'shēngrì', 'n.', 'birthday'], ['礼物', 'lǐwù', 'n.', 'gift'], ['蛋糕', 'dàngāo', 'n.', 'cake'], ['祝', 'zhù', 'v.', 'to wish']],
        grammar: [{ pattern: '给 + 人 + 动词 (do something for someone)', note: '给 introduces the person who receives something.', examples: ['我给他买了一个礼物。'] }],
        text1: ['A：明天是小李的生日，你买礼物了吗？', 'B：买了，我给他买了一本书。'],
        text2: ['今天是小李的生日。我们给他买了一个大蛋糕，祝他生日快乐！'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '周末活动', english: 'Weekend activities', classHours: '4–6',
        vocabulary: [['周末', 'zhōumò', 'n.', 'weekend'], ['打算', 'dǎsuàn', 'v./n.', 'to plan; plan'], ['爬山', 'pá shān', 'v.o.', 'to hike'], ['一起', 'yìqǐ', 'adv.', 'together']],
        grammar: [{ pattern: '打算 + 动词短语 (plan to do something)', note: '打算 comes before the verb phrase to talk about plans.', examples: ['这个周末你打算做什么？'] }],
        text1: ['A：这个周末你有空吗？', 'B：有空，我打算去爬山。'],
        text2: ['这个周末天气很好，我和朋友打算一起去爬山。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '买衣服', english: 'Shopping for clothes', classHours: '4–6',
        vocabulary: [['衣服', 'yīfu', 'n.', 'clothes'], ['试', 'shì', 'v.', 'to try'], ['颜色', 'yánsè', 'n.', 'color'], ['便宜', 'piányi', 'adj.', 'cheap']],
        grammar: [{ pattern: 'A 比 B + 形容词 (comparison)', note: '比 compares two things; the adjective comes last.', examples: ['这件比那件便宜。'] }],
        text1: ['A：这件衣服我可以试试吗？', 'B：可以。你喜欢什么颜色？'],
        text2: ['这件衣服的颜色很好看，可是有点儿贵。那件比这件便宜。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '坐地铁', english: 'Taking the subway', classHours: '3–4',
        vocabulary: [['地铁', 'dìtiě', 'n.', 'subway'], ['换', 'huàn', 'v.', 'to change'], ['站', 'zhàn', 'n.', 'station'], ['出口', 'chūkǒu', 'n.', 'exit']],
        grammar: [{ pattern: '先……再…… (first …, then …)', note: '先 marks the first action and 再 the next one.', examples: ['先坐地铁，再换公共汽车。'] }],
        text1: ['A：请问，去博物馆怎么走？', 'B：先坐二号线，再换四号线。'],
        text2: ['从学校到博物馆，先坐地铁，再走五分钟。博物馆在地铁站二号出口旁边。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '看病', english: 'Seeing a doctor', classHours: '4–6',
        vocabulary: [['看病', 'kàn bìng', 'v.o.', 'to see a doctor'], ['发烧', 'fāshāo', 'v.', 'to have a fever'], ['感冒', 'gǎnmào', 'v./n.', 'to catch a cold; cold'], ['药', 'yào', 'n.', 'medicine']],
        grammar: [{ pattern: '有点儿 + 形容词 (a bit; usually unwelcome)', note: '有点儿 before an adjective softens something unpleasant.', examples: ['我有点儿不舒服。'] }],
        text1: ['A：你怎么了？', 'B：我头疼，有点儿发烧。'],
        text2: ['小王感冒了，有点儿发烧。医生给他开了药，让他多喝水、多休息。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '租房子', english: 'Renting an apartment', classHours: '4–6',
        vocabulary: [['房子', 'fángzi', 'n.', 'house; apartment'], ['房租', 'fángzū', 'n.', 'rent'], ['安静', 'ānjìng', 'adj.', 'quiet'], ['离', 'lí', 'prep.', 'away from']],
        grammar: [{ pattern: 'A 离 B + 近/远 (distance)', note: '离 says how near or far one place is from another.', examples: ['我家离学校很近。'] }],
        text1: ['A：这个房子离地铁站远吗？', 'B：不远，走路五分钟。'],
        text2: ['我的新房子离学校很近，房租也不贵，周围很安静。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
    {
        title: '旅行计划', english: 'Travel plans', classHours: '4–6',
        vocabulary: [['旅行', 'lǚxíng', 'v.', 'to travel'], ['计划', 'jìhuà', 'n./v.', 'plan; to plan'], ['机票', 'jīpiào', 'n.', 'plane ticket'], ['酒店', 'jiǔdiàn', 'n.', 'hotel']],
        grammar: [{ pattern: '……还是……？ (choice question)', note: '还是 offers a choice in a question.', examples: ['你想去北京还是上海？'] }],
        text1: ['A：寒假你有什么旅行计划？', 'B：我打算去云南，机票已经买好了。'],
        text2: ['我们的旅行计划是先去北京，再去上海。机票和酒店都订好了。'],
        exercises1: [], exercises2: [], activity: [], homework: defaultHomework, selfCheck: [],
    },
];

const frontPages: { label: string; kind: SamplePageKind; lines: string[] }[] = [
    { label: 'cover', kind: 'cover', lines: ['汉语进阶 2', 'Mandarin Steps 2', '课本 Textbook'] },
    { label: 'ii', kind: 'front', lines: ['Mandarin Steps 2', 'A sample textbook for the VerbaSchola mockup.', 'Every page and line of text is invented sample data.'] },
    { label: 'iii', kind: 'guidance', lines: ['前言 Preface', '《汉语进阶》第二册适合学过一年左右汉语的学习者。', 'Mandarin Steps 2 is for learners who have studied Chinese for about one year.', '全书共八课，每课建议用 4–6 课时完成。', 'The book has eight lessons. Each lesson takes about 4–6 class hours.'] },
    { label: 'iv', kind: 'guidance', lines: ['教学理念 Teaching approach', '本书采用任务型教学。每课围绕一个真实任务：学生先听、读课文，再在课堂活动中用新学的语言完成任务。', 'This book uses task-based teaching. Each lesson centres on a real-life task: students first listen to and read the texts, then use the new language to complete the task in the class activity.', '学生做任务时，教师不要随时纠错；任务结束后再集中讲评。', 'While students work on a task, do not correct errors on the spot; review common errors after the task.'] },
    { label: 'v', kind: 'guidance', lines: ['语法与练习 Grammar and exercises', '语法部分只讲常用格式，不追求体系完整。', 'The grammar sections explain common patterns only; they do not aim to be a complete grammar.', '练习一用于课堂，练习二可以作为课后作业。', 'Exercises 1 are for class; Exercises 2 can be assigned as homework.'] },
    { label: 'vi', kind: 'guidance', lines: ['使用说明 How to use this book', '每课包括：课文一、生词一、课文二、生词二、语法、练习、课堂活动、课后作业和自我评估。', 'Each lesson has Text 1, New Words 1, Text 2, New Words 2, Grammar, Exercises, a Class activity, Homework and a Self-check.'] },
    { label: 'vii', kind: 'guidance', lines: ['课时安排建议 Suggested schedule', '第 1 课时：课文一、生词一', '第 2 课时：课文二、生词二', '第 3 课时：语法、练习一', '第 4 课时：课堂活动', '第 5–6 课时（可选）：复习、练习二', 'Period 1: Text 1 and New Words 1. Period 2: Text 2 and New Words 2. Period 3: Grammar and Exercises 1. Period 4: Class activity. Periods 5–6 (optional): review and Exercises 2.'] },
    { label: 'viii', kind: 'contents', lines: ['目录 Contents', '前言 Preface ...... iii', '使用说明 How to use this book ...... vi', ...units.slice(0, 4).map((unit, index) => `第${CHINESE_NUMBERS[index]}课 ${unit.title} ${unit.english} ...... ${index * UNIT_PAGES + 1}`)] },
    { label: 'ix', kind: 'contents', lines: ['目录（续）Contents (continued)', ...units.slice(4).map((unit, index) => `第${CHINESE_NUMBERS[index + 4]}课 ${unit.title} ${unit.english} ...... ${(index + 4) * UNIT_PAGES + 1}`), `生词总表 Vocabulary index ...... ${units.length * UNIT_PAGES + 1}`, `练习答案 Answer key ...... ${units.length * UNIT_PAGES + 5}`] },
    { label: 'x', kind: 'front', lines: ['人物介绍 Characters', '小王 Xiǎo Wáng：大学二年级学生 a second-year university student', '小李 Xiǎo Lǐ：小王的朋友 Xiao Wang\'s friend'] },
];

function withoutToneMarks(pinyin: string): string {
    return pinyin.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function vocabularyRows(vocabulary: SampleUnit['vocabulary'], first: number): string[] {
    return vocabulary.map(([hanzi, pinyin, part, meaning], index) => `${first + index}. ${hanzi} ${pinyin} ${part} ${meaning}`);
}

function unitPages(unit: SampleUnit, unitIndex: number): Omit<SamplePdfPage, 'pdfPage' | 'printedLabel'>[] {
    const name = `第${CHINESE_NUMBERS[unitIndex]}课 ${unit.title}`;
    const half = Math.ceil(unit.vocabulary.length / 2);
    const firstWords = unit.vocabulary.slice(0, half);
    const [g1, g2] = unit.grammar;
    const page = (kind: SamplePageKind, lines: string[], scanned = false, issues: OcrIssue[] = []) => ({ kind, text: lines.join('\n'), scanned, issues });
    const id = (suffix: string) => `u${unitIndex + 1}-${suffix}`;
    const firstPinyin = firstWords[0][1];
    // Scanned pages show the two failures seen in real OCR: lost tone marks and merged table rows.
    const vocabularyIssues: OcrIssue[] = unitIndex === 0
        ? [
            { id: id('tone-1'), message: 'Pinyin has no tone marks.', wrong: '饭馆 fanguan', right: '饭馆 fànguǎn' },
            { id: id('tone-2'), message: 'One syllable lost its tone mark.', wrong: 'diǎn cai', right: 'diǎn cài' },
            { id: id('row'), message: 'Two vocabulary rows were read as one line.', wrong: 'dumplings 6. 米饭', right: 'dumplings\n6. 米饭' },
        ]
        : [{ id: id('tone-1'), message: 'Pinyin has no tone marks.', wrong: `${firstWords[0][0]} ${withoutToneMarks(firstPinyin)}`, right: `${firstWords[0][0]} ${firstPinyin}` }];
    const exerciseIssues: OcrIssue[] = [
        { id: id('table'), message: 'This table was read by OCR. Check that each row keeps its own cells.' },
        ...(unitIndex === 0 ? [{ id: id('space'), message: 'A space was read inside a word.', wrong: '二十五 块', right: '二十五块' }] : []),
    ];
    return [
        page('opener', [name, `Lesson ${unitIndex + 1} ${unit.english}`, '学习目标 Objectives', ...(unit.selfCheck.length ? unit.selfCheck.map(line => `• ${line.slice(2)}`) : [`• 用本课生词谈论“${unit.title}” Talk about ${unit.english.toLowerCase()}`]), `建议课时：${unit.classHours} 课时`, `Suggested class time: ${unit.classHours} class hours`]),
        page('text', ['课文一 Text 1', ...unit.text1]),
        page('vocabulary', ['生词一 New Words 1', ...vocabularyRows(firstWords, 1)], true, vocabularyIssues),
        page('text', ['课文二 Text 2', ...unit.text2]),
        page('vocabulary', ['生词二 New Words 2', ...vocabularyRows(unit.vocabulary.slice(half), half + 1)]),
        page('grammar', ['语法一 Grammar 1', g1.pattern, g1.note, '例句 Examples', ...g1.examples.map((example, index) => `${'①②'[index]} ${example}`)]),
        page('grammar', g2
            ? ['语法二 Grammar 2', g2.pattern, g2.note, '例句 Examples', ...g2.examples.map((example, index) => `${'①②'[index]} ${example}`)]
            : ['语法练习 Grammar practice', `用“${g1.pattern.split(' (')[0]}”完成句子。Complete the sentences.`, '1. ________', '2. ________', '3. ________']),
        page('exercise', ['练习一 Exercises 1', ...(unit.exercises1.length ? unit.exercises1 : ['一、朗读生词 Read the new words aloud', unit.vocabulary.map(word => word[0]).join('　'), '二、回答问题 Answer the questions about Text 1'])]),
        page('exercise', ['练习二 Exercises 2', ...(unit.exercises2.length ? unit.exercises2 : ['三、看图说话 Describe the pictures', '图一 | 图二 | 图三'])], true, exerciseIssues),
        page('activity', ['课堂活动 Class activity', ...(unit.activity.length ? unit.activity : lessonActivity(unit.english))]),
        page('exercise', ['课后作业 Homework', ...unit.homework]),
        page('exercise', ['自我评估 Self-check', ...(unit.selfCheck.length ? unit.selfCheck : [`□ 我能用本课生词谈论“${unit.title}”。`, `□ 我会用“${g1.pattern.split(' (')[0]}”。`])]),
    ];
}

function backPages(): { kind: SamplePageKind; lines: string[] }[] {
    const entries = units.flatMap((unit, index) => unit.vocabulary.map(([hanzi, pinyin, , meaning]) => `${hanzi} ${pinyin} ${meaning} (${index + 1})`));
    const perPage = Math.ceil(entries.length / 4);
    const index = [0, 1, 2, 3].map(part => ({ kind: 'index' as const, lines: [part ? '生词总表（续）Vocabulary index (continued)' : '生词总表 Vocabulary index', ...entries.slice(part * perPage, (part + 1) * perPage)] }));
    const answers = [0, 1].map(part => ({ kind: 'index' as const, lines: [part ? '练习答案（续）Answer key (continued)' : '练习答案 Answer key', ...units.slice(part * 4, part * 4 + 4).map((unit, offset) => `第${CHINESE_NUMBERS[part * 4 + offset]}课 练习一：答案不唯一。Answers will vary.`)] }));
    return [...index, ...answers];
}

export function createSampleTextbook(id: string, name: string, sizeLabel: string): SamplePdfFile {
    const pages: SamplePdfPage[] = frontPages.map((page, index) => ({ pdfPage: index + 1, printedLabel: page.label, kind: page.kind, text: page.lines.join('\n'), scanned: false, issues: [] }));
    units.forEach((unit, unitIndex) => unitPages(unit, unitIndex).forEach((page, offset) => {
        const printed = unitIndex * UNIT_PAGES + offset + 1;
        pages.push({ ...page, pdfPage: FIRST_UNIT_PAGE + printed - 1, printedLabel: String(printed) });
    }));
    backPages().forEach(page => pages.push({ pdfPage: pages.length + 1, printedLabel: String(pages.length + 1 - FIRST_UNIT_PAGE + 1), kind: page.kind, text: page.lines.join('\n'), scanned: false, issues: [] }));

    const lastUnitPage = FIRST_UNIT_PAGE + units.length * UNIT_PAGES - 1;
    const sections: SamplePdfSection[] = [
        { id: 'preface', title: '前言', english: 'Preface', firstPage: 3, lastPage: 5, group: 'Front matter', forGuidance: true },
        { id: 'how-to-use', title: '使用说明', english: 'How to use this book', firstPage: 6, lastPage: 7, group: 'Front matter', forGuidance: true },
        { id: 'contents', title: '目录', english: 'Contents', firstPage: 8, lastPage: 9, group: 'Front matter' },
        ...units.map((unit, index) => ({
            id: `lesson-${index + 1}`, title: `第${CHINESE_NUMBERS[index]}课 ${unit.title}`, english: unit.english, group: 'Lessons' as const, classHours: unit.classHours,
            firstPage: FIRST_UNIT_PAGE + index * UNIT_PAGES, lastPage: FIRST_UNIT_PAGE + (index + 1) * UNIT_PAGES - 1,
        })),
        { id: 'index', title: '生词总表', english: 'Vocabulary index', firstPage: lastUnitPage + 1, lastPage: lastUnitPage + 4, group: 'Back matter' },
        { id: 'answers', title: '练习答案', english: 'Answer key', firstPage: lastUnitPage + 5, lastPage: lastUnitPage + 6, group: 'Back matter' },
    ];
    return { id, name, sizeLabel, pages, sections };
}

/** The page text as extraction returns it: a scanned page keeps each OCR misreading until it is fixed. */
export function extractedText(page: SamplePdfPage): string {
    return page.issues.reduce((text, issue) => issue.wrong && issue.right ? text.replace(issue.right, issue.wrong) : text, page.text);
}

/** "11-22, 25" for PDF pages 11 to 22 and 25. */
export function formatPageRanges(pages: number[]): string {
    const sorted = [...pages].sort((a, b) => a - b);
    const ranges: string[] = [];
    for (let index = 0; index < sorted.length; index += 1) {
        const first = sorted[index];
        while (index + 1 < sorted.length && sorted[index + 1] === sorted[index] + 1) index += 1;
        ranges.push(first === sorted[index] ? String(first) : `${first}-${sorted[index]}`);
    }
    return ranges.join(', ');
}

/** Ranges for reading, with en dashes: "11–22, 25". Typed ranges use hyphens. */
export function pageRangeLabel(pages: number[]): string {
    return formatPageRanges(pages).replace(/-/g, '–');
}

/** The printed page labels of a selection, such as "iii–vii" or "1–12, 15". */
export function printedRanges(file: SamplePdfFile, pages: number[]): string {
    return formatPageRanges(pages).split(', ').map(range => {
        const [first, last] = range.split('-').map(Number);
        const firstLabel = file.pages[first - 1]?.printedLabel ?? String(first);
        return last ? `${firstLabel}–${file.pages[last - 1]?.printedLabel ?? last}` : firstLabel;
    }).join(', ');
}
